import { useCallback } from 'react';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { EidEasySigningMethod } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { EidEasySignStatus } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import type { BulkProgress } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import createClient from '@eid-easy/eideasy-browser-client';
import * as Sentry from '@sentry/react';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	FTN_CALLBACK_PATH,
	isEidEasySandbox,
	startFtnTabSigning,
	toErrorKey,
	isUserInitiatedError,
} from './useEidEasySign.utils';
import type { BulkSignJobStatusRes } from '@dealstack/shared/lib/generated';
import { useBulkSignPolling } from './useBulkSignPolling';
import { makeTransportRequest } from './bulkSignTransport';
import { showBulkSignSkipNotices } from './bulkSignSkipNotices';
import type {
	BulkSignTransport,
	BulkSignMode,
	SignRequestFields,
	BulkSignRequestParams,
} from './bulkSignTransport';

interface EidEasyPrepareResponse {
	payload: {
		docId: string;
		clientId: string;
		baseUrl: string;
		skippedInstanceIds?: string[];
		witnessSkippedInstanceIds?: string[];
		deferredInstanceIds?: string[];
	};
}

interface EidEasyBulkCompleteResponse {
	payload: {
		status: string;
		results: Array<{ instanceId: string; signatureId: string; status: string }>;
	};
}

interface BulkSignInitiateResponse {
	payload: {
		jobId: string;
		totalDocuments: number;
		skippedInstanceIds?: string[];
		witnessSkippedInstanceIds?: string[];
		deferredInstanceIds?: string[];
	};
}

interface UseBulkFtnSignOptions {
	transport: BulkSignTransport;
	signMode: BulkSignMode;
	sentryFlowTag: string;
	queryKeyPrefix: string;
	onStatusChange: (status: EidEasySignStatus) => void;
	onError: (error: string) => void;
	onProgress: (progress: BulkProgress | null) => void;
	onComplete: () => void;
}

export const useBulkFtnSign = ({
	transport,
	signMode,
	sentryFlowTag,
	queryKeyPrefix,
	onStatusChange,
	onError,
	onProgress,
	onComplete,
}: UseBulkFtnSignOptions) => {
	const tMipManage = useTranslationMipManage();
	const request = useCallback(
		<T>(params: BulkSignRequestParams) => makeTransportRequest<T>(transport, params),
		[transport]
	);

	const handlePrepareReady = useCallback(
		async (data: BulkSignJobStatusRes) => {
			try {
				if (!data.docId || !data.clientId || !data.baseUrl) {
					throw new Error('Job completed but missing signing data');
				}

				const isSandbox = isEidEasySandbox(data.baseUrl);
				onStatusChange(EidEasySignStatus.AWAITING_SIGN);
				onProgress(null);

				const client = createClient({
					clientId: data.clientId,
					docId: data.docId,
					countryCode: 'FI',
					language: 'en',
					sandbox: isSandbox,
					redirectUri: `${window.location.origin}${FTN_CALLBACK_PATH}`,
				});

				await startFtnTabSigning(client);

				onStatusChange(EidEasySignStatus.COMPLETING);
				onProgress({ phase: 'completing', current: 0, total: data.totalDocuments });

				// TODO: polling activity while waiting for sign
				await request({
					endpoint: transport.buildEndpoint(
						'signed',
						polling.activeJobIdRef.current ?? ''
					),
					method: HTTP_METHOD.POST,
					body: JSON.stringify({}),
				});

				polling.startCompletePhase();
			} catch (err: unknown) {
				polling.stopPolling();
				if (!isUserInitiatedError(err)) {
					Sentry.captureException(err, {
						tags: { signingFlow: sentryFlowTag, phase: 'prepare-ready' },
					});
				}
				onError(toErrorKey(err));
				onProgress(null);
				onStatusChange(EidEasySignStatus.ERROR);
			}
		},
		[transport, sentryFlowTag, request]
	);

	const polling = useBulkSignPolling({
		buildStatusEndpoint: (jobId) => transport.buildEndpoint('status', jobId),
		requestOptions: transport.requestOptions,
		queryKeyPrefix,
		sentryFlowTag,
		callbacks: {
			onPrepareReady: handlePrepareReady,
			onCompleteFinished: () => {
				onStatusChange(EidEasySignStatus.DONE);
				onProgress(null);
				onComplete();
			},
			onFailed: (error) => {
				onError(error);
				onProgress(null);
				onStatusChange(EidEasySignStatus.ERROR);
			},
			onProgress: (progress) => onProgress(progress),
		},
	});

	const runPollingFlow = useCallback(
		async (signRequestFields: SignRequestFields, drawnSignatureBase64: string) => {
			const ftnCallbackUrl = `${window.location.origin}${FTN_CALLBACK_PATH}`;

			onStatusChange(EidEasySignStatus.PREPARING);
			onError('');

			try {
				const initiateResult = await request<BulkSignInitiateResponse>({
					endpoint: transport.buildEndpoint('initiate'),
					method: HTTP_METHOD.POST,
					body: JSON.stringify({
						...signRequestFields,
						method: EidEasySigningMethod.FTN,
						signatureRedirect: ftnCallbackUrl,
						drawnSignatureBase64,
					}),
				});

				const { jobId, totalDocuments } = initiateResult.payload;
				showBulkSignSkipNotices(initiateResult.payload, tMipManage);
				onProgress({ phase: 'preparing', current: 0, total: totalDocuments });
				polling.startPolling(jobId, 'prepare');
			} catch (err: unknown) {
				Sentry.captureException(err, {
					tags: { signingFlow: sentryFlowTag, phase: 'initiate' },
				});
				onError(toErrorKey(err));
				onProgress(null);
				onStatusChange(EidEasySignStatus.ERROR);
			}
		},
		[
			transport,
			sentryFlowTag,
			polling,
			onStatusChange,
			onError,
			onProgress,
			request,
			tMipManage,
		]
	);

	const runDirectFlow = useCallback(
		async (signRequestFields: SignRequestFields, drawnSignatureBase64: string) => {
			const ftnCallbackUrl = `${window.location.origin}${FTN_CALLBACK_PATH}`;

			onStatusChange(EidEasySignStatus.PREPARING);
			onError('');

			try {
				const prepareResult = await request<EidEasyPrepareResponse>({
					endpoint: transport.buildEndpoint('prepare'),
					method: HTTP_METHOD.POST,
					body: JSON.stringify({
						...signRequestFields,
						method: EidEasySigningMethod.FTN,
						signatureRedirect: ftnCallbackUrl,
						drawnSignatureBase64,
					}),
				});

				const { docId, clientId, baseUrl } = prepareResult.payload;
				showBulkSignSkipNotices(prepareResult.payload, tMipManage);
				const isSandbox = isEidEasySandbox(baseUrl);

				onStatusChange(EidEasySignStatus.AWAITING_SIGN);

				const client = createClient({
					clientId,
					docId,
					countryCode: 'FI',
					language: 'en',
					sandbox: isSandbox,
					redirectUri: `${window.location.origin}${FTN_CALLBACK_PATH}`,
				});

				await startFtnTabSigning(client);

				onStatusChange(EidEasySignStatus.COMPLETING);

				await request<EidEasyBulkCompleteResponse>({
					endpoint: transport.buildEndpoint('complete'),
					method: HTTP_METHOD.POST,
					body: JSON.stringify({ docId }),
				});

				onStatusChange(EidEasySignStatus.DONE);
				onComplete();
			} catch (err: unknown) {
				if (!isUserInitiatedError(err)) {
					Sentry.captureException(err, {
						tags: { signingFlow: sentryFlowTag, phase: 'direct-flow' },
					});
				}
				onError(toErrorKey(err));
				onStatusChange(EidEasySignStatus.ERROR);
			}
		},
		[transport, sentryFlowTag, onComplete, request, tMipManage]
	);

	const confirmFtn = useCallback(
		async (signRequestFields: SignRequestFields, drawnSignatureBase64: string) => {
			if (signMode === 'async') {
				await runPollingFlow(signRequestFields, drawnSignatureBase64);
			} else {
				await runDirectFlow(signRequestFields, drawnSignatureBase64);
			}
		},
		[runPollingFlow, runDirectFlow, signMode]
	);

	return {
		confirmFtn,
		stopPolling: polling.stopPolling,
	};
};
